import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { compose, keyBy, map } from 'lodash/fp';
import { createStructuredSelector } from 'reselect';
import { Embed, Report as PowerBiReport, Page } from 'powerbi-client';
import { pipe } from 'fp-ts/function';
import { LocalizedText } from '@mews/framework';
import { TextKey, ActionType, ArticlePath, createGuideLink } from '@mews-commander/enums';
import {
    asyncContainerFactory,
    ContentContainer,
    DefaultErrorHandler,
    ModalBodyWrapper,
    ModalWrapper,
    TextWithLinks,
} from '@mews-commander/ui';
import { Banner, IconName, Tab } from '@optimus-web/core';
import { createHandler } from '@mews/std';
import { ApplicationState } from '@mews-commander/types';
import { localizeText } from '@mews-commander/utils';
import { getCultureCode, getCurrentEnterpriseId, getLanguageCode, getCurrentUser } from '@mews-commander/core';
import { isCurrentProfileAdmin } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getPlatformScopeProfileId } from '@mews-commander/legacy-entities/src/ducks/scopes';
import {
    stopFetchingPowerBiEmbeddedToken,
    selectPage,
    loadReport,
    renderedReport,
    buttonClicked,
    selectionChanged,
    visualClicked,
    selectData,
    openImportAnalytics,
    persistCapturedBookmark,
    clearCapturedBookmark,
    violateDataIntegrity,
} from '../actions';
import {
    getEmbeddedToken,
    isFetching,
    hasFailed,
    getReportId,
    getEmbedUrl,
    isDataIntegrityConfirmed,
    showReceivableTrackingDisabledWarning,
} from '../selectors/core';
import { getCurrentAnalyticsCapturedBookmarks } from '../selectors/bookmarks';
import {
    getActiveTabName,
    getVisibleTabs,
    getVisibleActiveTabName,
    getFirstVisibleTab,
    getTabsFromReportPages,
    mapAnalyticsTypeTextKey,
} from '../utils';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { logError } from '../../app/actions/common';
import AnalyticsType from '../enums/analyticsType';
import {
    EmbedData,
    EmbedDataEvent,
    IntegrityViolationCredentials,
    IReportBookmark,
    WithAnalyticsType,
    FilterItem,
} from '../types';
import { OwnProps as ReportProps, Report } from './Report';

interface OwnProps {
    analyticsType: AnalyticsType;
}

interface PowerBiStateProps {
    embeddedToken?: string;
    embedUrl: string;
    reportId?: string;
    isFetching: boolean;
    hasFailed: boolean;
    errorMessage: React.ReactNode;
    isAdmin: boolean;
    capturedBookmark: IReportBookmark;
    enterpriseId: string;
    integrityConfirmed: boolean;
    platformScopeProfileId?: string;
    showReceivableTrackingDisabledWarning: boolean;
}

interface PowerBiDispatchProps {
    stopFetchingPowerBiEmbeddedToken: () => void;
    selectPage: ({ pageName, analyticsType }: { pageName: string } & WithAnalyticsType) => void;
    loadReport: ({ analyticsType }: WithAnalyticsType) => void;
    renderedReport: ({ analyticsType }: WithAnalyticsType) => void;
    buttonClicked: ({ analyticsType }: WithAnalyticsType) => void;
    selectionChanged: ({ report, page, visual }: EmbedData) => void;
    visualClicked: ({ report, page, visual }: EmbedData) => void;
    selectData: ({ report, page, visual }: EmbedData) => void;
    logError: ({ type, message, context }: { type: string; message: string; context?: object }) => void;
    openImportAnalytics: () => void;
    persistCapturedBookmark: ({
        analyticsType,
        capturedBookmark,
    }: WithAnalyticsType & { capturedBookmark: IReportBookmark }) => void;
    clearCapturedBookmark: ({ analyticsType }: WithAnalyticsType) => void;
    violateDataIntegrity: ({ currentUserId, powerBiUserId, enterpriseId, tab }: IntegrityViolationCredentials) => void;
}

type Props = OwnProps & PowerBiStateProps & PowerBiDispatchProps;

type State = $TSFixMe;

class PowerBi extends Component<Props, State> {
    report: PowerBiReport | undefined;

    state = {
        tabs: [],
    };

    pages: Record<string, Page> | null = null;

    dataIntegrityCheckInterval: number | null = null;
    currentPage: string | null = null;

    componentWillUnmount() {
        this.props.stopFetchingPowerBiEmbeddedToken();
        if (this.report) {
            this.report.off('loaded');
            this.report.off('pageChanged');
            this.report.off('dataSelected');
            this.report.off('error');
            this.report.off('rendered');
            this.report.off('buttonClicked');
            this.report.off('selectionChanged');
            this.report.off('visualClicked');
        }
        if (this.dataIntegrityCheckInterval) {
            window.clearInterval(this.dataIntegrityCheckInterval);
        }
    }

    startCheckingDataIntegrity() {
        void this.checkForDataIntegrity();
        this.dataIntegrityCheckInterval = window.setInterval(this.checkForDataIntegrity, 5000);
    }

    checkForDataIntegrity = async () => {
        if (this.report) {
            const pages = await this.report.getPages();
            const activePage = pages.find((page) => page.isActive);
            const visuals = await activePage?.getVisuals();

            if (visuals) {
                const visualWithUserId = visuals.find((visual) => visual.title.startsWith('UserId:'));

                if (visualWithUserId) {
                    const powerBiUserId = visualWithUserId.title.replace('UserId: ', '');
                    const { id: currentUserId } = getCurrentUser();

                    if (powerBiUserId !== currentUserId) {
                        const activeTabName = getVisibleActiveTabName(this.state.tabs);
                        const tab = this.state.tabs.find((t) => (t as $TSFixMe).name === activeTabName);
                        this.props.violateDataIntegrity({
                            currentUserId,
                            powerBiUserId,
                            enterpriseId: this.props.enterpriseId,
                            tab,
                        });
                    }
                }
            }
        }
    };

    handleReportLoaded = () => {
        const { analyticsType, capturedBookmark } = this.props;
        this.props.loadReport({ analyticsType });
        void this.updatePages().then(() => {
            if (this.report && capturedBookmark?.state) {
                void this.report.bookmarksManager.applyState(capturedBookmark.state);
            }
        });
        this.startCheckingDataIntegrity();
    };

    handleReportPageChanged = (e: EmbedDataEvent) => {
        const {
            detail: {
                newPage: { displayName },
            },
        } = e;
        this.currentPage = displayName;
        const { analyticsType } = this.props;
        this.props.selectPage({ pageName: displayName, analyticsType });
        void this.updatePages();
    };

    handleDataSelected = (e: EmbedDataEvent) => {
        const {
            detail: { report, page, visual },
        } = e;
        this.props.selectData({
            report: report.displayName,
            page: page.displayName,
            visual: `${visual.title}/${visual.name}`,
        });
    };

    handleError = (e: $TSFixMe) => {
        const error = e.detail;
        this.props.logError({
            type: 'powerbi',
            message: error.detailedMessage,
            context: error.technicalDetails,
        });
    };

    handleRendered = async () => {
        let reportFilters: FilterItem[] | undefined;
        let pageFilters: FilterItem[] | undefined;
        const { analyticsType } = this.props;
        const pages = await this.report?.getPages();
        const activePage = pages?.find((page) => page.isActive);

        try {
            reportFilters = (await this.report?.getFilters()) as FilterItem[];
        } catch (e) {
            reportFilters = undefined;
        }

        try {
            pageFilters = (await activePage?.getFilters()) as FilterItem[];
        } catch (e) {
            pageFilters = undefined;
        }

        this.props.renderedReport({ analyticsType, pageName: this.currentPage, reportFilters, pageFilters });
    };

    handleButtonClicked = (e: EmbedDataEvent) => {
        const { analyticsType } = this.props;
        const {
            detail: { title },
        } = e;
        this.props.buttonClicked({ analyticsType, pageName: this.currentPage, title });
    };

    handleSelectionChanged = (e: EmbedDataEvent) => {
        const {
            detail: { report, page, visual },
        } = e;
        const payload = {
            report: report.displayName,
            page: page.displayName,
            visual: 'no visual',
        };
        if (visual && visual.name !== null) {
            payload.visual = `${visual.title}/${visual.name}`;
        }
        this.props.selectionChanged(payload);
    };

    handleVisualClicked = (e: EmbedDataEvent) => {
        const {
            detail: { report, page, visual },
        } = e;
        this.props.visualClicked({
            report: report.displayName,
            page: page.displayName,
            visual: `${visual.title}/${visual.name}`,
        });
    };

    handleReportEmbedded = (report: Embed) => {
        report.on('loaded', this.handleReportLoaded);
        report.on('pageChanged', this.handleReportPageChanged);
        report.on('dataSelected', this.handleDataSelected);
        report.on('error', this.handleError);
        report.on('rendered', this.handleRendered);
        report.on('buttonClicked', this.handleButtonClicked);
        report.on('selectionChanged', this.handleSelectionChanged);
        report.on('visualClicked', this.handleVisualClicked);

        this.report = report as PowerBiReport;
    };

    handleTabClick = async (e: $TSFixMe, name: string) => this.pages && this.pages[name].setActive();

    handleImportClick = () => this.props.openImportAnalytics();

    handleSaveBookmarkClick = () => {
        if (this.report) {
            const { analyticsType } = this.props;
            void this.report.bookmarksManager
                .capture()
                .then((capturedBookmark) => this.props.persistCapturedBookmark({ analyticsType, capturedBookmark }));
        }
    };

    handleClearBookmarkClick = () => {
        const { analyticsType } = this.props;
        this.props.clearCapturedBookmark({ analyticsType });
    };

    updatePages = async () => {
        if (this.report) {
            return this.report.getPages().then((pages) => {
                this.pages = keyBy<Page>('name')(pages);

                const tabs = getTabsFromReportPages(this.props.analyticsType)(pages);

                const activeTabName = getActiveTabName(tabs);
                if (!activeTabName && this.pages && tabs.length) {
                    // @ts-expect-error $TSFixMeError: Property 'name' does not exist on type '{ visibility: { toString: ...; toFixed: ...; toExponential: ...; toPrecision: .....
                    const { name } = getFirstVisibleTab(tabs);
                    void this.pages[name].setActive();
                }

                this.setState({ tabs });
            });
        }

        return Promise.resolve();
    };

    renderTab = ({ name, displayName }: $TSFixMe) => (
        <Tab
            key={name}
            id={name}
            value={name}
            data-test-tab={displayName}
            label={<LocalizedText textKey={displayName} />}
        />
    );

    renderTabs = compose(map(this.renderTab), getVisibleTabs);

    printReport = async () => {
        if (this.report) {
            await this.report.print();
        }
    };

    render() {
        const activeTabName = getVisibleActiveTabName(this.state.tabs);
        const culture = getCultureCode();
        const language = getLanguageCode();

        const {
            embeddedToken,
            reportId,
            embedUrl,
            analyticsType,
            isAdmin,
            capturedBookmark,
            integrityConfirmed,
            platformScopeProfileId,
        } = this.props;

        const powerBiEmbeddedProps: ReportProps = {
            id: reportId,
            embedUrl,
            language,
            formatLocale: culture,
            accessToken: embeddedToken,
            onEmbedded: this.handleReportEmbedded,
            platformScopeProfileId,
        };

        const isAnalyticsImportVisible = isAdmin && analyticsType === AnalyticsType.Manager;

        const actions = [
            {
                type: ActionType.Link,
                actionId: 'makeDefault',
                text: <LocalizedText textKey={TextKey.AnalyticsFiltersMakeDefault} />,
                icon: IconName.MakeDefault,
                onClick: this.handleSaveBookmarkClick,
            },
            ...(capturedBookmark
                ? [
                      {
                          type: ActionType.Link,
                          actionId: 'clear',
                          text: <LocalizedText textKey={TextKey.AnalyticsFiltersClear} />,
                          icon: IconName.Broom,
                          onClick: this.handleClearBookmarkClick,
                      },
                  ]
                : []),
            ...(isAnalyticsImportVisible
                ? [
                      {
                          type: ActionType.Link,
                          actionId: 'import',
                          text: <LocalizedText textKey={TextKey.AnalyticsImport} />,
                          icon: IconName.Ftp,
                          onClick: this.handleImportClick,
                      },
                  ]
                : []),
            {
                type: ActionType.Link,
                actionId: 'print',
                text: <LocalizedText textKey={TextKey.Print} />,
                icon: IconName.Print,
                onClick: this.printReport,
            },
            {
                type: ActionType.Link,
                actionId: 'moreAbout',
                text: <LocalizedText textKey={TextKey.Help} />,
                icon: IconName.Help,
                url: createGuideLink(getArticlePathFromAnalyticsType(this.props.analyticsType)),
                target: '_blank',
            },
        ];

        const secondaryText = (
            <TextWithLinks
                links={[
                    {
                        id: 'here',
                        url: createGuideLink(ArticlePath.WhatIsAccountingConfiguration),
                        text: localizeText(TextKey.AgingBalanceReceivableTrackingDisabledLink),
                    },
                ]}
                text={localizeText(TextKey.AgingBalanceReceivableTrackingDisabledMessage)}
            />
        );

        return integrityConfirmed ? (
            <Fragment>
                <ModalHeader
                    // @ts-expect-error $TSFixMeError: No overload matches this call.
                    actions={actions}
                    title={<LocalizedText textKey={mapAnalyticsTypeTextKey[analyticsType]} />}
                    tabsConfig={{
                        activeTabValue: activeTabName,
                        handleTabClick: this.handleTabClick,
                        inline: true,
                        tabs: this.renderTabs(this.state.tabs),
                    }}
                />

                {analyticsType === AnalyticsType.AgingBalance && this.props.showReceivableTrackingDisabledWarning && (
                    <ContentContainer condensed>
                        <Banner
                            primaryText={<LocalizedText textKey={TextKey.AgingBalanceReceivableTrackingDisabled} />}
                            secondaryText={secondaryText}
                            variant="info"
                        />
                    </ContentContainer>
                )}

                <ModalBodyWrapper>
                    <Report {...powerBiEmbeddedProps} />
                </ModalBodyWrapper>
            </Fragment>
        ) : (
            <Fragment>
                <DefaultErrorHandler />
            </Fragment>
        );
    }
}

const getArticlePathFromAnalyticsType = createHandler<AnalyticsType, ArticlePath>(
    {
        [AnalyticsType.Manager]: ArticlePath.ManagerAnalytics,
        [AnalyticsType.Financial]: ArticlePath.FinancialAnalytics,
        [AnalyticsType.Outlet]: ArticlePath.OutletAnalytics,
        [AnalyticsType.Pickup]: ArticlePath.PickupAnalytics,
        [AnalyticsType.AgingBalance]: ArticlePath.AgingBalance,
        [AnalyticsType.DepositLedger]: ArticlePath.DepositLedger,
        [AnalyticsType.GuestLedger]: ArticlePath.GuestLedger,
    },
    ArticlePath.WhatIsAnalytics
);

export default pipe(
    PowerBi,
    asyncContainerFactory({ ContentElement: ModalWrapper }),
    connect<PowerBiStateProps, PowerBiDispatchProps, OwnProps, ApplicationState>(
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        createStructuredSelector({
            embeddedToken: getEmbeddedToken,
            embedUrl: getEmbedUrl,
            reportId: getReportId,
            isFetching,
            hasFailed,
            isAdmin: isCurrentProfileAdmin,
            capturedBookmark: getCurrentAnalyticsCapturedBookmarks,
            enterpriseId: getCurrentEnterpriseId,
            integrityConfirmed: isDataIntegrityConfirmed,
            platformScopeProfileId: getPlatformScopeProfileId,
            showReceivableTrackingDisabledWarning,
        }),
        {
            stopFetchingPowerBiEmbeddedToken,
            selectPage,
            loadReport,
            renderedReport,
            buttonClicked,
            selectionChanged,
            visualClicked,
            selectData,
            logError,
            openImportAnalytics,
            persistCapturedBookmark,
            clearCapturedBookmark,
            violateDataIntegrity,
        }
    )
) as unknown as React.ComponentType<OwnProps>;
